import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

export type RecordingState = 'idle' | 'recording' | 'recorded' | 'submitting' | 'submitted'

export function useAudioRecorder() {
  const [state, setState] = useState<RecordingState>('idle')
  const [seconds, setSeconds] = useState(0)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startingRef = useRef(false)

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
    for (const t of mediaRecorderRef.current?.stream.getTracks() ?? []) {
      t.stop()
    }
  }, [])

  useEffect(() => () => cleanup(), [cleanup])

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl)
    }
  }, [audioUrl])

  const startRecording = useCallback(async () => {
    if ((state !== 'idle' && state !== 'recorded') || startingRef.current) return
    startingRef.current = true
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        const b = new Blob(chunksRef.current, { type: recorder.mimeType })
        setBlob(b)
        setAudioUrl(URL.createObjectURL(b))
        setState('recorded')
      }

      mediaRecorderRef.current = recorder
      recorder.start()
      setSeconds(0)
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000)
      setState('recording')
    } catch {
      toast.error('Unable to access microphone')
    } finally {
      startingRef.current = false
    }
  }, [state, cleanup])

  const reset = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioUrl(null)
    setBlob(null)
    setSeconds(0)
    // setState('idle')
  }, [audioUrl])

  const resetToIdle = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioUrl(null)
    setBlob(null)
    setSeconds(0)
    setState('idle')
  }, [audioUrl])

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    mediaRecorderRef.current?.stop()
    for (const t of mediaRecorderRef.current?.stream.getTracks() ?? []) {
      t.stop()
    }
  }

  const markSubmitting = useCallback(() => setState('submitting'), [])
  const markSubmitted = useCallback(() => setState('submitted'), [])
  const markRecorded = useCallback(() => setState('recorded'), [])

  return {
    state,
    seconds,
    audioUrl,
    blob,
    startRecording,
    stopRecording,
    reset,
    resetToIdle,
    markSubmitting,
    markSubmitted,
    markRecorded
  }
}
